import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  coerceLocale,
  mapAuthoredText,
  pickAuthoredText,
  type AuthoredText,
  type Locale,
  planCloneBody,
  programCloneBody,
  resolvePlanTemplate,
  resolveProgramTemplate,
  type Plan,
  type Program,
  type ResolvedPlanTemplate,
  type ResolvedProgramTemplate,
} from "@jackedlogs/shared";

import type { ProgramApiFetch } from "../ProgramEditor/types";
import { writeError, type DurableWriteFn } from "../seams";
import { usePrograms } from "../Programs/usePrograms";
import { usePlans } from "../Plans/usePlans";
import { useTemplates } from "./useTemplates";
import { favoriteKey, useFavorites } from "./useFavorites";

// The Library-browser data layer (redesign track HR §S3): ONE hook that
// composes the existing shared list hooks — usePrograms (SWR + status
// mutations), usePlans, useTemplates — into the browse buckets, and owns
// the item actions (follow / clone) so mobile and web-rnw mount the same
// logic. NO new fetch paths: personal rows come from the same hooks the
// Plan surfaces already use; official rows are the /api/templates set.
// The coach bucket is a role-gated placeholder until the capabilities
// spine (PE-5) lights it up.

export type LibraryFilter =
  | "favorites"
  | "programs"
  | "workouts"
  | "personal"
  | "coach";

export type LibraryItem =
  | { key: string; kind: "program"; source: "personal"; program: Program }
  | { key: string; kind: "plan"; source: "personal"; plan: Plan }
  | {
      key: string;
      kind: "program";
      source: "official";
      template: ResolvedProgramTemplate;
    }
  | {
      key: string;
      kind: "plan";
      source: "official";
      template: ResolvedPlanTemplate;
    };

// PE-21 — a program's name may carry several languages. `locale` is the
// reader's; omitted, the chain starts at the program's author.
export const libraryItemName = (
  item: LibraryItem,
  locale?: Locale,
): string => {
  switch (item.source) {
    case "personal":
      return item.kind === "program"
        ? (pickAuthoredText(
            item.program.name,
            locale,
            item.program.authorLocale,
          ) ?? "")
        : item.plan.name;
    case "official":
      return item.template.name;
  }
};

/** The FAVORITE#<kind>#<id> identity of an item — null when the item
 *  isn't favoritable (templates, until PE-8 makes them real rows). */
export const libraryFavoriteKey = (item: LibraryItem): string | null =>
  item.source === "personal"
    ? favoriteKey(
        item.kind,
        item.kind === "program" ? item.program.id : item.plan.id,
      )
    : null;

/** Items visible under one filter-tab. Pure — shared by every mount. */
export const filterLibraryItems = (
  items: ReadonlyArray<LibraryItem>,
  filter: LibraryFilter,
  favoriteKeys: ReadonlySet<string> = new Set(),
): LibraryItem[] => {
  switch (filter) {
    case "favorites": {
      return items.filter((i) => {
        const key = libraryFavoriteKey(i);
        return key !== null && favoriteKeys.has(key);
      });
    }
    case "programs":
      return items.filter((i) => i.kind === "program");
    case "workouts":
      return items.filter((i) => i.kind === "plan");
    case "personal":
      return items.filter((i) => i.source === "personal");
    case "coach":
      // Placeholder until PE-5: assigned rows aren't queryable yet.
      return [];
  }
};

export type UseLibraryOptions = {
  apiFetch: ProgramApiFetch;
  /** Host write seam — mobile queues when offline, web sends only. */
  durableWrite: DurableWriteFn;
};

export function useLibrary(opts: UseLibraryOptions) {
  const { t, i18n } = useTranslation();
  const lang = coerceLocale(i18n.resolvedLanguage);

  const { apiFetch, durableWrite } = opts;
  const programsState = usePrograms({ apiFetch, durableWrite });
  const plansState = usePlans({ apiFetch, durableWrite });
  const templatesState = useTemplates({ apiFetch });
  const favoritesState = useFavorites({ apiFetch, durableWrite });

  const [cloningKey, setCloningKey] = useState<string | null>(null);
  const [cloneError, setCloneError] = useState<string | null>(null);
  const [cloneFlash, setCloneFlash] = useState<string | null>(null);
  // Items cloned since the browser mounted — keeps the action disabled
  // across the parent-list refetch race (same guard as TemplatesShelf).
  const [clonedKeys, setClonedKeys] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  const items = useMemo<LibraryItem[]>(() => {
    const out: LibraryItem[] = [];
    for (const tpl of templatesState.programTemplates) {
      out.push({
        key: `official/program/${tpl.id}`,
        kind: "program",
        source: "official",
        template: resolveProgramTemplate(tpl, lang),
      });
    }
    for (const tpl of templatesState.planTemplates) {
      out.push({
        key: `official/plan/${tpl.id}`,
        kind: "plan",
        source: "official",
        template: resolvePlanTemplate(tpl, lang),
      });
    }
    for (const program of programsState.programs) {
      out.push({
        key: `personal/program/${program.id}`,
        kind: "program",
        source: "personal",
        program,
      });
    }
    for (const plan of plansState.plans) {
      out.push({
        key: `personal/plan/${plan.id}`,
        kind: "plan",
        source: "personal",
        plan,
      });
    }
    return out;
  }, [
    templatesState.programTemplates,
    templatesState.planTemplates,
    programsState.programs,
    plansState.plans,
    lang,
  ]);

  // Names already in the personal lists — drives the templates'
  // "already cloned" disabled state (cross-session de-dup by resolved
  // name, the TemplatesShelf contract).
  const existingNames = useMemo(() => {
    const names = new Set<string>();
    for (const p of programsState.programs)
      names.add(pickAuthoredText(p.name, lang, p.authorLocale) ?? "");
    for (const p of plansState.plans) names.add(p.name);
    return names;
    // `lang` included: the de-dup compares RESOLVED names, so it has to
    // recompute when the reader's language changes.
  }, [programsState.programs, plansState.plans, lang]);

  const alreadyCloned = useCallback(
    (item: LibraryItem): boolean =>
      item.source === "official" &&
      (existingNames.has(item.template.name) || clonedKeys.has(item.key)),
    [existingNames, clonedKeys],
  );

  /** Clone any library item into the caller's partition. Templates keep
   *  their resolved name (de-dup above prevents doubles); personal rows
   *  get a localized "(copy)" suffix so the copy is tellable apart. */
  const cloneItem = useCallback(
    async (item: LibraryItem): Promise<void> => {
      if (cloningKey) return;
      setCloningKey(item.key);
      setCloneError(null);
      setCloneFlash(null);
      const copySuffix = t("library.copySuffix", { defaultValue: "(copy)" });
      try {
        let name: AuthoredText | string;
        let result;
        if (item.kind === "program") {
          const src =
            item.source === "personal" ? item.program : item.template;
          // Rename EVERY language the source carries: a clone of a
          // translated program must not arrive collapsed to one.
          name =
            item.source === "personal"
              ? mapAuthoredText(src.name, (n) => `${n} ${copySuffix}`)
              : src.name;
          result = await durableWrite({
            method: "POST",
            path: "/api/me/programs",
            body: programCloneBody(src, { name }),
          });
          void programsState.refreshPrograms();
        } else {
          const src = item.source === "personal" ? item.plan : item.template;
          name =
            item.source === "personal"
              ? `${src.name} ${copySuffix}`
              : src.name;
          result = await durableWrite({
            method: "POST",
            path: "/api/me/plans",
            body: planCloneBody(src, { name }),
          });
          void plansState.refresh();
        }
        const err = writeError(result);
        if (err !== null) {
          // Generic localized message to the user; the raw code (e.g.
          // api_error_409) is a breadcrumb, not UI copy.
          console.warn("[library] clone_failed", err.message);
          setCloneError(
            t("library.errClone", {
              defaultValue: "Could not clone. Try again.",
            }),
          );
          return;
        }
        // A queued clone counts as cloned: it is durable, and telling the
        // athlete otherwise would be the lie DX-2 removes.
        setCloneFlash(
          // PE-21 — resolve before interpolating: i18n accepts any value
          // and a per-locale object would surface as "[object Object]".
          t("templates.cloned", { name: pickAuthoredText(name, lang) ?? "" }),
        );
        setClonedKeys((prev) => new Set(prev).add(item.key));
      } finally {
        setCloningKey(null);
      }
    },
    // The deps below name the EXACT members this callback calls; eslint
    // wants the whole `programsState`/`plansState` object, which would
    // re-create the callback whenever any unrelated part of that state
    // changes. Pre-existing, surfaced when packages/ui got a linter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      cloningKey,
      t,
      durableWrite,
      programsState.refreshPrograms,
      plansState.refresh,
    ],
  );

  /** Status moves on the full axis — follow (→active), unfollow/publish
   *  (→archived). The Library owns these since HR-S4 retired the
   *  ProgramsPlanView rows. */
  const setProgramStatus = useCallback(
    async (program: Program, status: Program["status"]): Promise<Error | null> =>
      programsState.setProgramStatus(program.id, status),
    // Exact member listed on purpose — see the note above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [programsState.setProgramStatus],
  );

  /** Delete a personal program/plan (management moved here in HR-S4).
   *  Templates are never deletable — they aren't the caller's rows. */
  const deleteItem = useCallback(
    async (item: LibraryItem): Promise<Error | null> => {
      if (item.source !== "personal") return null;
      return item.kind === "program"
        ? programsState.deleteProgram(item.program.id)
        : plansState.deletePlan(item.plan.id);
    },
    // Exact members listed on purpose — see the note above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [programsState.deleteProgram, plansState.deletePlan],
  );

  /** Drop stale clone feedback (the host clears on tab switch so a
   *  "Cloned X" flash never outlives its context). */
  const clearFeedback = useCallback(() => {
    setCloneFlash(null);
    setCloneError(null);
  }, []);

  return {
    items,
    filterLibraryItems,
    personalStatus: programsState.programsStatus,
    plansStatus: plansState.status,
    templatesStatus: templatesState.status,
    cloningKey,
    cloneError,
    cloneFlash,
    alreadyCloned,
    cloneItem,
    setProgramStatus,
    deleteItem,
    clearFeedback,
    favoriteKeys: favoritesState.favoriteKeys,
    favoritesStatus: favoritesState.favoritesStatus,
    favoritePendingKey: favoritesState.favoritePendingKey,
    toggleFavorite: favoritesState.toggleFavorite,
  };
}

export type LibraryState = ReturnType<typeof useLibrary>;
